
import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { ApiUrl } from "../../../ApiUrl";
import { useNavigate } from "react-router-dom";
import "./StudentTimeTable.css";

const getCurrentWeekOfMonth = () => {
  const now = new Date();
  const date = now.getDate();
  return Math.ceil(date / 7);
};

const TimeTable = () => {
  const navigate = useNavigate();
  const [timeTableData, setTimeTableData] = useState([]);
  const [viewMode, setViewMode] = useState("Weekly");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentWeekIndex, setCurrentWeekIndex] = useState(() => getCurrentWeekOfMonth());

  const daysOfWeek = ["Mon", "Tue", "Wed", "Thurs", "Fri", "Sat"];
  const periods = ["1st", "2nd", "3rd", "4th", "5th", "6th", "7th"];
  const normalizeSubjectValue = (value) =>
    String(value || "").trim().toLowerCase();
  const derivedSubjectOptions = Array.from(
    new Map(
      timeTableData
        .filter((item) => item.subject || item.subject_description)
        .map((item) => [
          item.subject || item.subject_description,
          item.subject_description || item.subject,
        ])
    )
  ).map(([value, label]) => ({
    value,
    label,
  }));

  const filteredTimeTableData = selectedSubject
    ? timeTableData.filter(
        (item) => {
          const normalizedSelectedSubject = normalizeSubjectValue(selectedSubject);
          return (
            normalizeSubjectValue(item.subject) === normalizedSelectedSubject ||
            normalizeSubjectValue(item.subject_description) === normalizedSelectedSubject
          );
        }
      )
    : timeTableData;

  useEffect(() => {
    fetchTimeTableData();
  }, []);

  const fetchTimeTableData = async () => {
    try {
      const studentId = sessionStorage.getItem("userId");
      const orgId = localStorage.getItem("orgId") || "";
      const branchId = localStorage.getItem("branchId") || "";
      const academicYearId = localStorage.getItem("academicSessionId") || "";

      if (!studentId) {
        setError("Student session details are missing. Please log in again.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      const queryParams = new URLSearchParams();
      if (academicYearId) queryParams.append("academic_year_id", academicYearId);
      if (orgId) queryParams.append("org_id", orgId);
      if (branchId) queryParams.append("branch_id", branchId);

      const response = await fetch(
        `${ApiUrl.apiurl}STUDENT_PROFILE/GetStudentTimeTable/${studentId}/?${queryParams.toString()}`
      );
      const contentType = response.headers.get("content-type") || "";
      const payload = contentType.includes("application/json")
        ? await response.json()
        : null;

      if (!response.ok || !payload || payload.message !== "Success") {
        throw new Error(payload?.message || "Unable to load time table.");
      }

      if (Array.isArray(payload.data?.timetable)) {
        setTimeTableData(payload.data.timetable);
      } else {
        setTimeTableData([]);
      }

      const classId = payload.data?.class_id;
      const sectionId = payload.data?.section_id;

      if (classId) {
        try {
          const fetchSubjectOptions = async (includeSection = false) => {
            const subjectQuery = new URLSearchParams();
            if (academicYearId) subjectQuery.append("academic_year_id", academicYearId);
            if (orgId) subjectQuery.append("org_id", orgId);
            if (branchId) subjectQuery.append("branch_id", branchId);
            subjectQuery.append("class_id", classId);
            if (includeSection && sectionId) {
              subjectQuery.append("section_id", sectionId);
            }

            const subjectResponse = await fetch(
              `${ApiUrl.apiurl}SUBJECTS/GetSubjectListBasedOnClassAndSection/?${subjectQuery.toString()}`
            );

            if (subjectResponse.status === 204) {
              return [];
            }

            const subjectPayload = await subjectResponse.json();
            if (
              subjectResponse.ok &&
              subjectPayload?.message?.toLowerCase() === "success" &&
              Array.isArray(subjectPayload.data)
            ) {
              return subjectPayload.data
                .map((subject) => ({
                  value:
                    subject.subjectdescription ||
                    subject.subject_code ||
                    subject.subjectcode ||
                    "",
                  label:
                    subject.subjectdescription ||
                    subject.subject_code ||
                    subject.subjectcode ||
                    "",
                }))
                .filter((subject) => subject.value && subject.label);
            }

            return [];
          };

          let fullSubjectOptions = await fetchSubjectOptions(false);
          if (!fullSubjectOptions.length && sectionId) {
            fullSubjectOptions = await fetchSubjectOptions(true);
          }

          if (fullSubjectOptions.length) {
            setSubjectOptions(fullSubjectOptions);
          } else {
            setSubjectOptions([]);
          }
        } catch (subjectError) {
          console.error("Error fetching subject filter options:", subjectError);
          setSubjectOptions([]);
        }
      } else {
        setSubjectOptions([]);
      }
    } catch (error) {
      console.error("Error fetching timetable:", error);
      setTimeTableData([]);
      setSubjectOptions([]);
      setError(error.message || "Unable to load time table.");
    } finally {
      setLoading(false);
    }
  };

  const getTimeTableEntry = (day, period) => {
    return filteredTimeTableData.find(
      (item) =>
        item.schedule_day?.toLowerCase().startsWith(day.toLowerCase()) &&
        (item.class_period_number || item.class_period || "")
          .toString()
          .replace(/\D/g, "") === period.replace(/\D/g, "")
    );
  };

  const showWeekNavigation = () => {
    if (viewMode === "Monthly") {
      return false;
    }

    return true;
  };

  const goToPreviousWeek = () => {
    setCurrentWeekIndex((current) => Math.max(1, current - 1));
  };

  const goToNextWeek = () => {
    const maxWeeks = getWeeksInMonth();
    setCurrentWeekIndex((current) => Math.min(maxWeeks, current + 1));
  };

  const getWeekHeading = () => {
    const weekDates = getDatesForWeek(currentWeekIndex);
    const validDates = weekDates.filter(Boolean);

    if (!validDates.length) {
      return "Week of";
    }

    const startDate = validDates[0];
    const monthName = startDate.toLocaleString(undefined, {
      month: "short",
    });

    return `Week of ${monthName} ${startDate.getDate()}`;
  };

  //  Get number of weeks in the current month
  const getWeeksInMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const used = firstDay.getDay() + lastDay.getDate();
    return Math.ceil(used / 7);
  };

  //  Get dates for a given week (only within current month)
  const getDatesForWeek = (weekIndex) => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Find the first Monday of the month
    const firstMonday = new Date(firstDay);
    while (firstMonday.getDay() !== 1) {
      firstMonday.setDate(firstMonday.getDate() + 1);
    }

    // Week start date
    const weekStart = new Date(firstMonday);
    weekStart.setDate(firstMonday.getDate() + (weekIndex - 1) * 7);

    // Collect Mon–Sat but restrict to current month
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      return d.getMonth() === month ? d : null; //  Exclude outside dates
    });
  };

  //  Format date like 15Sep
  const formatDate = (date) => {
    if (!date) return "";
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${date.getDate()}${monthNames[date.getMonth()]}`;
  };

  const renderTable = (week = null) => {
    const dates = week
      ? getDatesForWeek(week)
      : getDatesForWeek(getCurrentWeekOfMonth());

    return (
      <div key={week || "weekly"} className="table-responsive mb-4">
        {week && <h6 className="fw-bold mb-2">Week {week}</h6>}
        <table className="table align-middle text-center">
          <thead>
            <tr>
              <th
                style={{
                  width: "80px",
                  background: "#bbbbbbff",
                  color: "#000",
                }}
              >
                Day
              </th>
              {periods.map((period, idx) => (
                <th
                  key={idx}
                  style={{
                    background: "#bbbbbbff",
                    color: "#000",
                    fontWeight: "600",
                  }}
                >
                  {period}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {daysOfWeek.map((day, dayIdx) => (
              <tr key={dayIdx}>
                <td
                  className="fw-bold"
                  style={{
                    background: "#E6F0FA",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  {day}
                  <br />
                  <small style={{ fontWeight: "normal", color: "#555" }}>
                    {dates[dayIdx] ? formatDate(dates[dayIdx]) : ""}
                  </small>
                </td>
                {periods.map((period, idx) => {
                  const entry = getTimeTableEntry(day, period);
                  return (
                    <td key={idx}>
                      <div
                        className="d-flex"
                        style={{
                          minWidth: "100px",
                          minHeight: "145px",
                          borderRadius: "8px",
                          backgroundColor:
                            idx === periods.length - 1
                              ? "rgba(239,170,170,0.40)"
                              : "rgba(118,147,0,0.17)",
                          fontSize: "13px",
                          fontWeight: "500",
                          textAlign: "left",
                          padding: entry ? "10px 12px" : "0",
                          alignItems: entry ? "flex-start" : "center",
                          justifyContent: entry ? "flex-start" : "center",
                          lineHeight: "1.35",
                        }}
                      >
                        {entry ? (
                          <div>
                            <div><strong>Teacher Name:</strong> {entry.teacher_name || "-"}</div>
                            <div><strong>Session:</strong> {entry.session || "-"}</div>
                            <div><strong>Course:</strong> {entry.course || "-"}</div>
                            <div><strong>Dept:</strong> {entry.department || "-"}</div>
                            <div><strong>Sec:</strong> {entry.section || "-"}</div>
                            <div>
                              <strong>Period:</strong>{" "}
                              {entry.class_period || period}
                              {entry.class_period_description &&
                              entry.class_period_description !== entry.class_period
                                ? ` (${entry.class_period_description})`
                                : ""}
                            </div>
                            <div>
                              <strong>Sub:</strong>{" "}
                              {entry.subject_description || entry.subject || "-"}
                            </div>
                          </div>
                        ) : (
                          ""
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="container-fluid">
      <div className="row justify-content-center">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body">
              {/* Header */}
              <div className="student-timetable-header mb-4">
                <button
                  className="btn btn-danger"
                  onClick={() => navigate("/student/dashboards")}
                >
                  Close
                </button>
                <h5 className="student-timetable-title">TIME TABLE</h5>
                <div className="student-timetable-actions d-flex align-items-center gap-2 flex-wrap justify-content-end">
                  <select
                    className="form-select"
                    style={{ minWidth: "160px" }}
                    value={viewMode}
                    onChange={(e) => setViewMode(e.target.value)}
                  >
                    <option value="Weekly">Weekly</option>
                    <option value="Monthly">Monthly</option>
                  </select>
                  <select
                    className="form-select"
                    style={{ minWidth: "180px" }}
                    value={selectedSubject}
                    onChange={(e) => setSelectedSubject(e.target.value)}
                  >
                    <option value="">All Subjects</option>
                    {(subjectOptions.length ? subjectOptions : derivedSubjectOptions).map((subjectOption) => (
                      <option
                        key={subjectOption.value}
                        value={subjectOption.value}
                      >
                        {subjectOption.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {showWeekNavigation() && (
                <div className="d-flex justify-content-center align-items-center gap-3 mb-4">
                  <button
                    type="button"
                    className="btn btn-outline-info rounded-pill px-4"
                    onClick={goToPreviousWeek}
                  >
                    Previous
                  </button>
                  <span className="fw-bold text-center text-dark">
                    {getWeekHeading()}
                  </span>
                  <button
                    type="button"
                    className="btn btn-outline-info rounded-pill px-4"
                    onClick={goToNextWeek}
                  >
                    Next
                  </button>
                </div>
              )}

              {error ? <div className="alert alert-danger">{error}</div> : null}
              {loading ? (
                <div className="text-center py-4">Loading time table...</div>
              ) : (
                <>
                  {viewMode === "Weekly" && renderTable(currentWeekIndex)}
                  {viewMode === "Monthly" &&
                    Array.from({ length: getWeeksInMonth() }, (_, i) =>
                      renderTable(i + 1)
                    )}
                </>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeTable;

